import { useEffect, useState } from "react";

import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Modal from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import usePartProMessageChain from "../../data/usePartProMessagesChain";
import { IPartProConversation } from "../../models/PartProMessage";

interface PartProMessageChainProps {
  open: boolean;
  setOpen: any;
  conversation: IPartProConversation | undefined;
}

export default function PartProMessageChain({ conversation, open, setOpen }: PartProMessageChainProps) {
  const { messages, sendReply, markRead } = usePartProMessageChain(conversation);
  const [body, setBody] = useState("");

  // Opening the thread counts as reading it - clears the unread highlight and the sidebar badge.
  useEffect(() => {
    if (open && conversation?.unread) {
      markRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversation?.id]);

  const sendMessage = () => {
    sendReply.mutate({ body: body },
      {
        onSuccess: () => {
          setBody("");
        }
      })
  };

  if (messages.isLoading)
    return <Modal open={open} setOpen={setOpen} width={"max-w-[90vw]"}>
      <LoadingWheel />
    </Modal>;

  return <Modal open={open} setOpen={setOpen} width={"max-w-[90vw]"}>
    <div className="mt-4 bg-white overflow-auto">
      <h1 className="text-2xl font-semibold">Messages from {conversation?.buyerName ?? "buyer"}</h1>
      <h2 className="text-md">{conversation?.listingTitle ?? conversation?.subject}</h2>
      {(conversation?.partId || conversation?.vehicleId || conversation?.orderNumber) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6 mt-1 text-sm">
          {conversation?.partId && (
            <button
              type="button"
              onClick={() => openInNewTab(`/stock/${conversation.partId}`)}
              className="text-primary-600 hover:underline"
            >
              {conversation.partTagNumber ? `Part · Tag ${conversation.partTagNumber}` : "View part"}
            </button>
          )}
          {conversation?.vehicleId && (
            <button
              type="button"
              onClick={() => openInNewTab(`/vehicles/${conversation.vehicleId}`)}
              className="text-primary-600 hover:underline"
            >
              {["Vehicle", conversation.vehicleVrm, conversation.vehicleDescription]
                .filter(Boolean)
                .join(" · ")}
            </button>
          )}
          {conversation?.orderNumber && (
            <span className="text-gray-600">Order {conversation.orderNumber}</span>
          )}
        </div>
      )}
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          <tr className="bg-primary-50 text-gray-800">
            <th></th>
            <th className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left" scope="col">Date & Time</th>
            <th className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left" scope="col">Message</th>
          </tr>
        </thead>
        <tbody>
          {
            messages.data?.data.map((message) => <tr key={message.id} className={"hover:bg-gray-200" + (message.sender === "VENDOR" ? " bg-blue-100" : "")}>
              <td className="h-7 w-7">{message.sender === "VENDOR" ? <ArrowUpTrayIcon /> : <ArrowDownTrayIcon />}</td>
              <td className="px-3 py-3 border text-sm">{formatDateTime(message.createdAt, DateTime.DATETIME_SHORT)}</td>
              <td className="px-3 py-3 border text-sm whitespace-pre-line">{message.body}</td>
            </tr>)
          }
        </tbody>
      </table>
      <div className="mt-2 py-3 gap-5">
        <h2 className="text-lg font-semibold mb-2">Send a reply</h2>
        <textarea className="min-w-full bg-primary-50 border-gray-300 min-h-[10rem]" value={body} onChange={x => setBody(x.target.value)}></textarea>
        <button className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 mt-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 w-40 h-10 disabled:opacity-50" disabled={sendReply.isLoading || !body.trim()} onClick={() => sendMessage()}>
          {sendReply.isLoading ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  </Modal>
}
